import { useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import MoreBtnMembers from "../Components/MoreBtnMembers.jsx";
import { ReactComponent as DownBracket } from "../Images/down_bracket.svg";
import MemberAlarm from "../Modals/MemberAlarm.jsx";
import MemberCheck from "../Modals/MemberCheck.jsx";
import { cCode_to_nation } from "../Utils/numberUtils.js";
import {
  requestMemberActivation,
  requestMemberDeletion,
  requestMemberInactivation,
  requestMemberInfoUpdate,
} from "./Members.rowActions.js";

const formatMemberRegTime = (regTime) => {
  const normalizedRegTime = regTime ?? "";
  const [date = "", time = ""] = normalizedRegTime.split(" ");

  return { date, time };
};

const renderMemberRowCell = ({
  column,
  userId,
  userIdArr,
  handleSelect,
  page,
  offset,
  index,
  ranking,
  secretCode,
  isEditing,
  canManageMemberInfo,
  editingMemberState,
  departmentOptions,
  onEditDepartmentChange,
  departmentName,
  nickName,
  email,
  totalStep,
  prevMonthStep,
  currentMonthStep,
  regTime,
  cCode,
  onEditMemberTypeChange,
  relationLabel,
  isActionColumnCompact,
  onCancelEdit,
  onSaveEdit,
  isMoreOpen,
  onToggleMore,
  onCloseMore,
  onStartEdit,
  clickActive,
  onOpenInactiveModal,
  onOpenActiveModal,
  onOpenLeaveModal,
}) => {
  switch (column.key) {
    case "select":
      return (
        <p key={column.key} className="center">
          <RowSelectCheckbox
            type="checkbox"
            checked={userIdArr.includes(userId)}
            onChange={handleSelect}
          />
        </p>
      );
    case "no":
      return (
        <p key={column.key} className="center">
          {(page - 1) * offset + index + 1}
        </p>
      );
    case "ranking":
      return (
        <p key={column.key} className="center">
          {ranking?.toLocaleString()}위
        </p>
      );
    case "secretCode":
      return (
        <div key={column.key} className="leftalign memberTextCell">
          <span>{secretCode ? secretCode : "-"}</span>
        </div>
      );
    case "departmentName":
      return isEditing && canManageMemberInfo ? (
        <SelectCell key={column.key}>
          <InlineSelect
            value={editingMemberState?.editDepartmentId ?? "__none__"}
            onChange={(e) => onEditDepartmentChange(e.target.value)}
          >
            {departmentOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </InlineSelect>
          <SelectArrow>
            <DownBracket stroke="#888888" />
          </SelectArrow>
        </SelectCell>
      ) : (
        <div key={column.key} className="leftalign memberTextCell">
          <span>{departmentName ? departmentName : "없음"}</span>
        </div>
      );
    case "nickName":
      return (
        <div key={column.key} className="leftalign memberTextCell">
          <span>{nickName}</span>
        </div>
      );
    case "email":
      return (
        <div key={column.key} className="leftalign memberTextCell">
          <span title={email ?? ""}>{email ?? ""}</span>
        </div>
      );
    case "totalStep":
      return (
        <p key={column.key} className="leftalign">
          {totalStep?.toLocaleString()}
        </p>
      );
    case "prevMonthStep":
      return (
        <p key={column.key} className="leftalign">
          {(prevMonthStep ?? 0)?.toLocaleString()}
        </p>
      );
    case "currentMonthStep":
      return (
        <p key={column.key} className="leftalign">
          {(currentMonthStep ?? 0)?.toLocaleString()}
        </p>
      );
    case "regTime":
      const { date, time } = formatMemberRegTime(regTime);

      return (
        <div key={column.key} className="leftalign memberDateCell" title={regTime}>
          <span>{date}</span>
          <span>{time}</span>
        </div>
      );
    case "nation":
      return (
        <p key={column.key} className="leftalign">
          {cCode_to_nation(cCode)}
        </p>
      );
    case "relation":
      return isEditing ? (
        <SelectCell key={column.key}>
          <InlineSelect
            value={editingMemberState?.editMemberType ?? "1"}
            onChange={(e) => onEditMemberTypeChange(e.target.value)}
          >
            <option value="1">임직원</option>
            <option value="2">지인</option>
          </InlineSelect>
          <SelectArrow>
            <DownBracket stroke="#888888" />
          </SelectArrow>
        </SelectCell>
      ) : (
        <p key={column.key} className="leftalign">
          {relationLabel}
        </p>
      );
    case "actions":
      return (
        <ActionCell key={column.key} $isCompact={isActionColumnCompact}>
          {canManageMemberInfo && isEditing ? (
            <ActionButtons>
              <CancelButton onClick={onCancelEdit}>취소</CancelButton>
              <SaveButton onClick={onSaveEdit}>저장</SaveButton>
            </ActionButtons>
          ) : (
            <MoreBtnMembers
              isClicked={isMoreOpen}
              onToggle={onToggleMore}
              onClose={onCloseMore}
              option1={canManageMemberInfo ? "편집" : ""}
              option2={clickActive ? "비활성화" : "활성화"}
              option3={clickActive ? "" : "탈퇴"}
              option1Click={canManageMemberInfo ? onStartEdit : null}
              option2Click={
                clickActive ? onOpenInactiveModal : onOpenActiveModal
              }
              option3Click={clickActive ? null : onOpenLeaveModal}
            />
          )}
        </ActionCell>
      );
    default:
      return null;
  }
};

const MemberRow = ({
  userId,
  ranking,
  secretCode,
  departmentName,
  rownum,
  index,
  page,
  offset,
  nickName,
  email,
  totalStep,
  prevMonthStep,
  currentMonthStep,
  regTime,
  cCode,
  memberType,
  memberColumns,
  editingMemberState,
  departmentList,
  clickActive,
  userIdArr,
  setUserIdArr,
  isMoreOpen,
  onToggleMore,
  onCloseMore,
  isEditing,
  canManageMemberInfo,
  onStartEdit,
  onFinishEdit,
  onMemberUpdated,
  onEditDepartmentChange,
  onEditMemberTypeChange,
  isActionColumnCompact,
  memberGridTemplate,
  memberTableMinWidth,
}) => {
  const navigate = useNavigate();
  const [clickMemberActive, setClickMemberActive] = useState(false);
  const [clickMemberInactive, setClickMemberInactive] = useState(false);
  const [clickMemberLeave, setClickMemberLeave] = useState(false);
  const [alarmState, setAlarmState] = useState({
    show: false,
    title: "",
    description: "",
  });
  const relationLabel =
    memberType === 1 ? "임직원" : memberType === 2 ? "지인" : "-";
  const departmentOptions = [
    { value: "__none__", label: "없음" },
    ...departmentList.map((department) => ({
      value: String(department.companyDepartmentId),
      label: department.departmentName,
    })),
  ];

  const handleSelect = () => {
    setUserIdArr((prev) => {
      if (prev.includes(userId)) {
        return prev.filter((item) => item !== userId);
      }

      return [...prev, userId];
    });
  };

  const updateActive = async () => {
    try {
      const res = await requestMemberActivation({
        userId,
        navigate,
      });
      if (res.code === 200) {
        setAlarmState({
          show: true,
          title: "구성원 활성화 완료",
          description: "선택한 구성원이 활성화 되었습니다.",
        });
      } else if (res.code === 1011) {
        setAlarmState({
          show: true,
          title: "구성원 활성화 실패",
          description: "구성원 용량이 충분한지 확인해주세요. ",
        });
      }
    } catch (error) {
      console.error("구성원 활성화 중 에러:", error);
    }
  };

  const updateInActive = async () => {
    try {
      const res = await requestMemberInactivation({
        userId,
        navigate,
      });
      if (res.code === 200) {
        setAlarmState({
          show: true,
          title: "구성원 비활성화 완료",
          description: "선택한 구성원이 비활성화 되었습니다.",
        });
        setClickMemberInactive(false);
      } else if (res.code === 1011) {
        setAlarmState({
          show: true,
          title: "구성원 비활성화 실패",
          description: "다시 한 번 시도해주세요.",
        });
        setClickMemberInactive(false);
      }
    } catch (error) {
      console.error("구성원 활성화 중 에러:", error);
    }
  };

  const deleteMember = async () => {
    try {
      const data = await requestMemberDeletion({
        userId,
        navigate,
      });

      if (data.code === 200) {
        setAlarmState({
          show: true,
          title: "구성원 탈퇴 완료",
          description: "선택한 구성원이 탈퇴되었습니다.",
        });
        setClickMemberLeave(false);
      } else if (data.code === 1011) {
        setAlarmState({
          show: true,
          title: "구성원 탈퇴 실패",
          description: "하루 최대 10명까지 탈퇴시킬 수 있습니다.",
        });
        setClickMemberLeave(false);
      }
    } catch (error) {
      console.error("기업회원 탈퇴 중 에러: ", error);
    }
  };

  const updateMemberInfo = async () => {
    try {
      const res = await requestMemberInfoUpdate({
        userId,
        editingMemberState,
        navigate,
      });

      if (!res) {
        return;
      }

      if (res.code === 200) {
        onFinishEdit();
        await onMemberUpdated?.();
      } else {
        setAlarmState({
          show: true,
          title: "구성원 정보 수정 실패",
          description: "다시 한 번 시도해주세요.",
        });
      }
    } catch (error) {
      console.error("구성원 정보 수정 중 에러:", error);
      setAlarmState({
        show: true,
        title: "구성원 정보 수정 실패",
        description: "다시 한 번 시도해주세요.",
      });
    }
  };

  const memberRowCellContext = {
    userId,
    userIdArr,
    handleSelect,
    page,
    offset,
    index,
    ranking,
    secretCode,
    isEditing,
    canManageMemberInfo,
    editingMemberState,
    departmentOptions,
    onEditDepartmentChange,
    departmentName,
    nickName,
    email,
    totalStep,
    prevMonthStep,
    currentMonthStep,
    regTime,
    cCode,
    onEditMemberTypeChange,
    relationLabel,
    isActionColumnCompact,
    onCancelEdit: onFinishEdit,
    onSaveEdit: updateMemberInfo,
    isMoreOpen,
    onToggleMore,
    onCloseMore,
    onStartEdit,
    clickActive,
    onOpenInactiveModal: () => setClickMemberInactive(true),
    onOpenActiveModal: () => setClickMemberActive(true),
    onOpenLeaveModal: () => setClickMemberLeave(true),
  };

  return (
    <MemberData
      $gridTemplate={memberGridTemplate}
      $minWidth={memberTableMinWidth}
    >
      {clickMemberActive && (
        <MemberCheck
          type="활성화"
          handleApply={updateActive}
          handleClickX={() => setClickMemberActive(false)}
        />
      )}
      {clickMemberInactive && (
        <MemberCheck
          type="비활성화"
          handleApply={updateInActive}
          handleClickX={() => setClickMemberInactive(false)}
        />
      )}
      {clickMemberLeave && (
        <MemberCheck
          type="탈퇴"
          handleApply={deleteMember}
          handleClickX={() => setClickMemberLeave(false)}
        />
      )}
      {alarmState.show && (
        <MemberAlarm
          title={alarmState.title}
          description={alarmState.description}
          handleClickX={() =>
            setAlarmState({ show: false, title: "", description: "" })
          }
        />
      )}
      {memberColumns.map((column) =>
        renderMemberRowCell({
          column,
          ...memberRowCellContext,
        })
      )}
    </MemberData>
  );
};

const MemberData = styled.div`
  width: max-content;
  min-width: ${(props) => props.$minWidth};
  height: 56px;
  box-shadow: inset 0 -1px 0 0 #e4e4e4;
  display: grid;
  grid-template-columns: ${(props) => props.$gridTemplate};
  color: #222222;
  position: relative;
  background-color: #ffffff;
  &:hover {
    background-color: #f5f5f5;
  }
  .memberTextCell {
    min-width: 0;
    overflow: hidden;
  }
  .memberTextCell > span {
    display: block;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  > p {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .memberDateCell {
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;
    gap: 2px;
    line-height: 1.2;
  }
  .memberDateCell > span {
    display: block;
    white-space: nowrap;
  }
  .circle {
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background-color: #04bf00;
  }
`;

const SelectCell = styled.div`
  display: flex;
  align-items: center;
  position: relative;
  z-index: 3;
  margin: 0 14px;
  min-width: 0;
`;

const SelectArrow = styled.div`
  position: absolute;
  top: 50%;
  right: 12px;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  z-index: 4;
`;

const RowSelectCheckbox = styled.input`
  width: 20px;
  height: 20px;
`;

const InlineSelect = styled.select`
  width: 100%;
  min-width: 72px;
  height: 38px;
  padding: 0 36px 0 12px;
  border-radius: 8px;
  border: 1px solid #d9d9d9;
  background: #ffffff;
  font-family: "notoSans-medium";
  font-size: 14px;
  color: #222222;
  position: relative;
  z-index: 3;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
`;

const SaveButton = styled.button`
  all: unset;
  min-width: 52px;
  height: 32px;
  padding: 0 12px;
  border-radius: 8px;
  background: #1167ed;
  color: #ffffff;
  text-align: center;
  cursor: pointer;
`;

const ActionButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ActionCell = styled.div`
  display: flex;
  width: 100%;
  justify-content: ${(props) => (props.$isCompact ? "center" : "flex-end")};
  align-items: center;
  padding-right: ${(props) => (props.$isCompact ? "0" : "12px")};
  box-sizing: border-box;

  > * {
    margin-left: ${(props) => (props.$isCompact ? "0" : "auto")};
  }
`;

const CancelButton = styled.button`
  all: unset;
  min-width: 52px;
  height: 32px;
  padding: 0 12px;
  border-radius: 8px;
  box-shadow: inset 0 0 0 1px #d9d9d9;
  background: #ffffff;
  color: #555555;
  text-align: center;
  cursor: pointer;
`;

export default MemberRow;
